import { UseFormSetValue } from 'react-hook-form';
import { parse, states } from 'mrz';
import i18n from '../../../../../plugins/i18n/i18n';
import { countries, getCountries } from '../../../../../constants/objects';
import { formatApiDateToInput } from '../../../../../utils/date';
import { FormValues } from '../../Interfaces';
import { hasValidMrzFirstRow } from './readMrzFromImage';

export type MrzFormFields = {
  name?: string;
  passportNo?: string;
  dateOfBirth?: string;
  expiryDate?: string;
  gender?: 'male' | 'female';
  nationality?: string;
  passportIssueCountry?: string;
};

const ISO_NAME_ALIASES: Record<string, string> = {
  USA: 'U.S',
  GBR: 'UK',
  D: 'Germany',
  DEU: 'Germany',
  BMU: 'Bermudas',
  BOL: 'Bolivia',
  BRN: 'Brunei',
  CPV: 'Cape Green',
  CIV: 'Costa do Marfim',
  CZE: 'Czech republic',
  DJI: 'Djibuti',
  ERI: 'Eritreia',
  GHA: 'Gana',
  GRD: 'Granada',
  GUY: 'Guiana',
  IRN: 'Iran',
  KOR: 'South Korea',
  PRK: 'North Korea',
  LAO: 'Laos',
  LSO: 'Lesoto',
  LBN: 'Líbano',
  LBY: 'Líbia',
  FSM: 'Micronesia',
  MDA: 'Moldávia',
  PSE: 'Palestine',
  RWA: 'Ruanda',
  RUS: 'Russia',
  LCA: 'Santa Lúcia',
  SGP: 'Singapura',
  SYR: 'Syria',
  SSD: 'Southern Sudan',
  TWN: 'Taiwan',
  TZA: 'Tanzania',
  TTO: 'Trinidad e Tobago',
  TUR: 'Türkiye',
  VAT: 'Vatican',
  VEN: 'Venezuela',
  VIR: 'US Virgin Islands',
  VGB: 'british virgin islands'
};

const normalizeCountry = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\bthe\b/g, '')
    .replace(/[^a-z]/g, '');

const asText = (value: unknown): string => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
    return String((value as { value: unknown }).value || '').trim();
  }
  return String(value).trim();
};

const yyMMddToInput = (value: string, kind: 'birth' | 'expiry'): string => {
  const digits = value.replace(/[^\d]/g, '');
  if (digits.length < 6) {
    return '';
  }
  const yy = Number(digits.slice(0, 2));
  const month = digits.slice(2, 4);
  const day = digits.slice(4, 6);
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  if (monthNumber < 1 || monthNumber > 12 || dayNumber < 1 || dayNumber > 31) {
    return '';
  }

  const nowYear = new Date().getFullYear();
  let year = 2000 + yy;
  if (kind === 'birth' && year > nowYear) {
    year -= 100;
  }
  if (kind === 'expiry' && year < nowYear - 15) {
    year += 100;
  }

  return formatApiDateToInput(`${year}-${month}-${day}`);
};

const matchCountryName = (englishName: string): string => {
  if (!englishName) {
    return '';
  }

  const englishList = countries.en;
  const normalizedTarget = normalizeCountry(englishName);
  const englishIndex = englishList.findIndex((item) => normalizeCountry(item.name) === normalizedTarget);

  const localizedList = getCountries(i18n.language);

  if (englishIndex >= 0 && localizedList[englishIndex]) {
    return localizedList[englishIndex].name;
  }

  return localizedList.find((item) => normalizeCountry(item.name) === normalizedTarget)?.name || '';
};

const countryNameFromIso3 = (isoOrName: string): string => {
  if (!isoOrName) {
    return '';
  }

  const iso3 = isoOrName.replace(/</g, '').toUpperCase();
  const alias = ISO_NAME_ALIASES[iso3];
  if (alias) {
    const aliased = matchCountryName(alias);
    if (aliased) {
      return aliased;
    }
  }

  const englishName = (states as Record<string, string>)[iso3] || iso3;
  return matchCountryName(englishName);
};

const humanizeName = (value: string) =>
  value
    .split(/[\s<]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');

export const mapMrzParseToForm = (result: ReturnType<typeof parse>): MrzFormFields => {
  const fields = result.fields || {};
  const firstName = asText(fields.firstName);
  const lastName = asText(fields.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const sex = asText(fields.sex).toLowerCase();
  const nationalityCode = asText(fields.nationality);
  const issuingCode = asText(fields.issuingState);
  const documentNumber = asText(result.documentNumber || fields.documentNumber).replace(/</g, '');

  const mapped: MrzFormFields = {};

  const rowOneAligned = hasValidMrzFirstRow(result);

  if (fullName && rowOneAligned) {
    mapped.name = humanizeName(fullName);
  }
  if (documentNumber) {
    mapped.passportNo = documentNumber;
  }
  const birth = yyMMddToInput(asText(fields.birthDate), 'birth');
  if (birth) {
    mapped.dateOfBirth = birth;
  }
  const expiry = yyMMddToInput(asText(fields.expirationDate), 'expiry');
  if (expiry) {
    mapped.expiryDate = expiry;
  }
  if (sex === 'male' || sex === 'female') {
    mapped.gender = sex;
  }
  const nationality = countryNameFromIso3(nationalityCode);
  if (nationality) {
    mapped.nationality = nationality;
  }
  const issuingCountry = rowOneAligned ? countryNameFromIso3(issuingCode) : '';
  if (issuingCountry) {
    mapped.passportIssueCountry = issuingCountry;
  }

  return mapped;
};

export const applyMrzToShareholder = (
  setValue: UseFormSetValue<FormValues>,
  index: number,
  fields: MrzFormFields
) => {
  const options = { shouldDirty: true, shouldTouch: true, shouldValidate: false } as const;

  (Object.keys(fields) as (keyof MrzFormFields)[]).forEach((key) => {
    const value = fields[key];
    if (!value) {
      return;
    }
    setValue(`shareholders.${index}.${key}`, value, options);
  });
};
